/**
 * Create competition dialog — preserved edit at v1 (brief Q11).
 *
 * Ports the shape from spectator-ui's `AdminCompetitionCreatePage.vue` to
 * React + shadcn. Posts to the existing `POST /v1/admin/competitions` handler
 * which already writes an `admin_audit_log` row (S2 backend instrumentation).
 *
 * League switch (#355): EPL 26/27 renders numeric gameweek chips; WC renders the
 * eight tournament scope codes (MD1..F) with availability driven by the
 * earliest fixture in each scope. Scopes whose earliest kickoff is in the
 * past are disabled — the backend would reject them anyway.
 */

import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { api } from '@/api/client';
import {
  useCreateCompetition,
  type AdminGameKey,
  type CompetitionFormat,
  type CompetitionProductType,
  type CreateCompetitionInput,
} from './api';

interface CreateCompetitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CurrentRoundResponse {
  current_round?: number;
  round?: number;
}

interface FixtureRow {
  scope_code: string | null;
  kickoff: string;
}

interface FixturesListResponse {
  data: FixtureRow[];
}

const TOTAL_EPL_ROUNDS = 38;

/**
 * WC scope catalogue — mirrors `backend/src/leagues/registry.ts WC_SCOPES`.
 * Hardcoded here because the admin app doesn't import backend registry. Keep
 * in sync if scope codes ever change (rare; tied to FIFA bracket structure).
 */
const WC_SCOPES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'MD1', label: 'Group Stage · MD1' },
  { code: 'MD2', label: 'Group Stage · MD2' },
  { code: 'MD3', label: 'Group Stage · MD3' },
  { code: 'R32', label: 'Round of 32' },
  { code: 'R16', label: 'Round of 16' },
  { code: 'QF', label: 'Quarter Final' },
  { code: 'SF', label: 'Semi Final' },
  { code: 'F', label: 'Final & 3P' },
];

const LEAGUE_OPTIONS: ReadonlyArray<{ key: AdminGameKey; label: string }> = [
  { key: 'football_epl2627', label: 'Premier League 26/27' },
  { key: 'football_wc26', label: 'Fantopy Cup 2026 (archived compatible)' },
];

export function CreateCompetitionDialog({ open, onOpenChange }: CreateCompetitionDialogProps) {
  const [competitionType, setCompetitionType] = useState<CompetitionProductType>('fantasy');
  const [gameKey, setGameKey] = useState<AdminGameKey>('football_epl2627');
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<CompetitionFormat>('ONE_OFF');
  const [official, setOfficial] = useState(true);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [singleRound, setSingleRound] = useState(1);
  const [multiRounds, setMultiRounds] = useState<number[]>([]);
  const [singleScope, setSingleScope] = useState<string>('MD1');
  const [multiScopes, setMultiScopes] = useState<string[]>([]);
  const [maxEntrants, setMaxEntrants] = useState<string>('');
  const [entryDeadline, setEntryDeadline] = useState<string>('');
  const [currentRound, setCurrentRound] = useState(1);
  const [scopeEarliest, setScopeEarliest] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const isWC = gameKey === 'football_wc26';
  const isPredictionCompetition = competitionType === 'predictions';
  const create = useCreateCompetition();

  // EPL: pull current round once when the dialog opens / EPL is re-selected.
  useEffect(() => {
    if (!open || isWC) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const res = await api.get<CurrentRoundResponse>(
          '/v1/fixtures/current-round?game_key=football_epl2627',
        );
        if (cancelled) return;
        const r = res.round ?? res.current_round ?? 1;
        if (r >= 1) {
          setCurrentRound(r);
          setSingleRound((s) => (s < r ? r : s));
        }
      } catch {
        // Fall back to round 1; the dropdown still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isWC]);

  // WC: fetch all fixtures once and aggregate earliest kickoff per scope so we
  // can disable past scopes. WC has ~64 fixtures total — fits one page.
  useEffect(() => {
    if (!open || !isWC) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const res = await api.get<FixturesListResponse>(
          '/v1/fixtures?game_key=football_wc26&limit=400',
        );
        if (cancelled) return;
        const earliest: Record<string, string> = {};
        for (const fx of res.data ?? []) {
          if (!fx.scope_code) continue;
          const cur = earliest[fx.scope_code];
          if (!cur || fx.kickoff < cur) earliest[fx.scope_code] = fx.kickoff;
        }
        setScopeEarliest(earliest);
      } catch {
        // Non-fatal; we render all chips and the backend will reject past scopes.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isWC]);

  const futureRounds = useMemo(
    () => Array.from({ length: TOTAL_EPL_ROUNDS }, (_, i) => i + 1).filter((r) => r >= currentRound),
    [currentRound],
  );

  const futureScopes = useMemo(() => {
    const now = Date.now();
    return WC_SCOPES.map((s) => {
      const earliest = scopeEarliest[s.code];
      const earliestMs = earliest ? Date.parse(earliest) : null;
      // Align with backend: it rejects when lockAt (= earliest - 1h) is past,
      // so gate on the same threshold to avoid 400s at the boundary.
      const inPast = earliestMs !== null && earliestMs - 60 * 60 * 1000 <= now;
      // Available if either we have no fixture data yet, or earliest is in the future.
      const available = !inPast;
      return { ...s, available, earliest };
    });
  }, [scopeEarliest]);

  // Default single-scope to first available when WC is selected.
  useEffect(() => {
    if (!isWC) return;
    const firstAvailable = futureScopes.find((s) => s.available);
    if (firstAvailable && !futureScopes.find((s) => s.code === singleScope)?.available) {
      setSingleScope(firstAvailable.code);
    }
  }, [isWC, futureScopes, singleScope]);

  const isMultiRound = format === 'SERIES';
  const selectedRounds = isMultiRound ? multiRounds : [singleRound];
  const selectedScopes = isMultiRound ? multiScopes : [singleScope];
  const hasSelection = isWC ? selectedScopes.length > 0 : selectedRounds.length > 0;
  const canSubmit = title.trim().length > 0 && hasSelection && !create.isPending;

  function toggleRound(r: number) {
    setMultiRounds((prev) => {
      const idx = prev.indexOf(r);
      if (idx >= 0) return prev.filter((x) => x !== r);
      return [...prev, r].sort((a, b) => a - b);
    });
  }

  function toggleScope(code: string) {
    setMultiScopes((prev) => {
      const idx = prev.indexOf(code);
      if (idx >= 0) return prev.filter((x) => x !== code);
      // Keep insertion order — backend orders by registry scope.order.
      return [...prev, code];
    });
  }

  function selectAllRemaining() {
    if (isWC) {
      setMultiScopes(futureScopes.filter((s) => s.available).map((s) => s.code));
    } else {
      setMultiRounds(futureRounds);
    }
  }

  function reset() {
    setCompetitionType('fantasy');
    setGameKey('football_epl2627');
    setTitle('');
    setFormat('ONE_OFF');
    setOfficial(true);
    setVisibility('public');
    setMultiRounds([]);
    setMultiScopes([]);
    setMaxEntrants('');
    setEntryDeadline('');
    setError(null);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setError(null);
    const input: CreateCompetitionInput = {
      title: title.trim(),
      competition_type: competitionType,
      game_key: gameKey,
      format,
      official,
      visibility,
      max_entrants: maxEntrants ? Number(maxEntrants) : undefined,
      entry_deadline: entryDeadline || undefined,
      ...(isWC ? { scope_codes: selectedScopes } : { rounds: selectedRounds }),
    };
    try {
      await create.mutateAsync(input);
      reset();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create competition');
    }
  }

  const description = isPredictionCompetition
    ? 'Predictions are retired in fresh-start; use archived WC screens only for review.'
    : isWC
      ? 'Configure a compatible archived World Cup fantasy competition.'
      : 'Configure a free-to-play Premier League 26/27 fantasy competition. Rounds beyond the current EPL round are eligible.';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create competition</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Field label="Competition type">
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { key: 'fantasy', label: 'Fantasy' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setCompetitionType(opt.key)}
                  className={cn(
                    'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                    competitionType === opt.key
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="League">
            <div className="flex gap-2">
              {LEAGUE_OPTIONS.map((opt) => {
                const archivedOnly = opt.key === 'football_wc26';
                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={archivedOnly}
                    title={archivedOnly ? 'Archived World Cup competitions are view-only in fresh-start.' : undefined}
                    onClick={() => {
                      if (!archivedOnly) setGameKey(opt.key);
                    }}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      gameKey === opt.key
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:text-foreground',
                      archivedOnly && 'cursor-not-allowed opacity-50 hover:text-muted-foreground',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Title">
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isWC ? 'Archived World Cup fantasy arena' : 'Round 1 Premier League 26/27 Arena'}
            />
          </Field>

          <Field label="Format">
            <div className="flex gap-2">
              {(['ONE_OFF', 'SERIES'] as CompetitionFormat[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={cn(
                    'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                    format === f
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f === 'ONE_OFF' ? 'ONE-OFF' : 'SERIES'}
                </button>
              ))}
            </div>
          </Field>

          {isWC ? (
            isMultiRound ? (
              <Field
                label="Stages"
                hint={
                  <button
                    type="button"
                    onClick={selectAllRemaining}
                    className="text-xs text-primary hover:underline"
                  >
                    Select all remaining
                  </button>
                }
              >
                <div className="flex flex-wrap gap-1">
                  {futureScopes.map((s) => {
                    const active = multiScopes.includes(s.code);
                    return (
                      <button
                        key={s.code}
                        type="button"
                        disabled={!s.available}
                        onClick={() => s.available && toggleScope(s.code)}
                        title={s.label + (s.available ? '' : ' — already kicked off')}
                        className={cn(
                          'rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                          !s.available
                            ? 'cursor-not-allowed border-border/40 text-muted-foreground/50'
                            : active
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {s.code}
                      </button>
                    );
                  })}
                </div>
              </Field>
            ) : (
              <Field label="Stage">
                <select
                  value={singleScope}
                  onChange={(e) => setSingleScope(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground"
                >
                  {futureScopes.map((s) => (
                    <option key={s.code} value={s.code} disabled={!s.available}>
                      {s.label}
                      {!s.available ? ' — already kicked off' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )
          ) : isMultiRound ? (
            <Field
              label="Rounds"
              hint={
                <button
                  type="button"
                  onClick={selectAllRemaining}
                  className="text-xs text-primary hover:underline"
                >
                  Select all remaining
                </button>
              }
            >
              <div className="flex flex-wrap gap-1">
                {futureRounds.map((r) => {
                  const active = multiRounds.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRound(r)}
                      className={cn(
                        'rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                        active
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      R{r}
                    </button>
                  );
                })}
              </div>
            </Field>
          ) : (
            <Field label="Round">
              <select
                value={singleRound}
                onChange={(e) => setSingleRound(Number(e.target.value))}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground"
              >
                {futureRounds.map((r) => (
                  <option key={r} value={r}>
                    Round {r}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Max entrants (optional)">
              <Input
                type="number"
                min={2}
                value={maxEntrants}
                onChange={(e) => setMaxEntrants(e.target.value)}
                placeholder="No limit"
              />
            </Field>
            {isMultiRound ? (
              <Field label="Entry deadline (optional)">
                <Input
                  type="datetime-local"
                  value={entryDeadline}
                  onChange={(e) => setEntryDeadline(e.target.value)}
                />
              </Field>
            ) : (
              <div />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={official}
                onChange={(e) => setOfficial(e.target.checked)}
              />
              <span>Official competition</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={visibility === 'private'}
                onChange={(e) => setVisibility(e.target.checked ? 'private' : 'public')}
              />
              <span>Private (invite-only)</span>
            </label>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {hint}
      </div>
      {children}
    </label>
  );
}
